import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';

export type ButtonVariant = 'primary' | 'outline' | 'danger' | 'success' | 'secondary';
export type ButtonSize    = 'sm' | 'md' | 'lg';

/**
 * Generic branded button.
 * All colours are driven by CSS custom properties set by ThemeService,
 * so re-theming per tenant requires zero component changes.
 *
 * Usage:
 *   <app-button label="Submit" variant="primary" (clicked)="submit()"></app-button>
 *   <app-button label="Cancel" variant="outline" (clicked)="cancel()"></app-button>
 *   <app-button label="Delete" variant="danger" icon="trash" [loading]="busy"></app-button>
 */
@Component({
  selector: 'app-button',
  templateUrl: './app-button.component.html',
  styleUrls: ['./app-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppButtonComponent {
  @Input() label: string = '';
  @Input() routerLink: string | any[] | null = null;
  @Input() icon: string = '';
  @Input() title: string = '';
  @Input() ariaLabel: string = '';
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'md';
  @Input() iconOnly: boolean = false;
  @Input() iconOnlyBorderless: boolean = false;
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() fullWidth: boolean = false;

  @Output() clicked = new EventEmitter<MouseEvent>();

  get hostClass(): string {
    const parts: string[] = ['app-btn', `app-btn--${this.variant}`, `app-btn--${this.size}`];
    if (this.fullWidth) parts.push('app-btn--full');
    if (this.iconOnly) parts.push('app-btn--icon-only');
    if (this.iconOnlyBorderless) parts.push('app-btn--icon-only-borderless');
    if (this.disabled || this.loading) parts.push('app-btn--disabled');
    return parts.join(' ');
  }

  handleClick(e: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(e);
    }
  }
}
